import React from 'react';
import { motion } from 'framer-motion';
import { Check, Droplets, Sparkles, ShieldCheck, Car } from 'lucide-react';
type ServiceTier = {
  title: string;
  description: string;
  icon: React.ReactNode;
  color: 'blue' | 'green';
  features: string[];
  priceSm: string;
  priceSuv: string;
  popular?: boolean;
};
const services: ServiceTier[] = [{
  title: 'Basic Wash',
  description: '2 Car Minimum for Basic Washes Only',
  icon: <Car className="w-6 h-6" data-id="element-226" />,
  color: 'green',
  features: ['Exterior Wash', 'Light Wipe Down', 'Vacuum', 'Clean Rims', 'Dress Tires', 'Clean Windows'],
  priceSm: '$90',
  priceSuv: '$105'
}, {
  title: 'Wash & Wax',
  description: 'A thorough exterior wash with hybrid ceramic protection for lasting shine',
  icon: <Droplets className="w-6 h-6" data-id="element-227" />,
  color: 'blue',
  features: ['Exterior Wash', 'Window Cleaning', 'Vacuum', 'Light Wipedown Interior', 'Tire Dressing', 'Hybrid Ceramic Wax'],
  priceSm: '$160',
  priceSuv: '$180'
}, {
  title: 'Full Detail',
  description: 'Our most popular package — complete interior and exterior restoration',
  icon: <Sparkles className="w-6 h-6" data-id="element-228" />,
  color: 'green',
  features: ['Includes Interior Detail', 'Exterior Wash', 'Clay Bar Treatment', 'Clean Rims', 'Dress Tires', 'Hybrid Ceramic Wax', 'Deep Vacuum'],
  priceSm: '$300',
  priceSuv: '$350',
  popular: true
}, {
  title: 'Interior Detail',
  description: 'Deep clean, shampoo, and sanitize every surface inside your vehicle',
  icon: <ShieldCheck className="w-6 h-6" data-id="element-229" />,
  color: 'blue',
  features: ['Shampoo Seats', 'Deep Clean Interior', 'Vacuum', 'Shampoo Floor Mats', 'Steam & Sanitize'],
  priceSm: '$180',
  priceSuv: '$195'
}];
export function Services() {
  return <section className="w-full bg-black py-16 px-4" id="services" data-id="element-230">
      <div className="max-w-7xl mx-auto" data-id="element-231">
        <div className="text-center mb-12" data-id="element-232">
          <h2 className="text-5xl md:text-6xl font-black uppercase mb-4" data-id="element-233">
            <span className="bg-clip-text text-transparent bg-gradient-to-b from-yellow-300 via-yellow-400 to-yellow-500" data-id="element-234">
              Mobile Auto Detailing Packages
            </span>
          </h2>
          <div className="h-1 w-24 bg-red-600 mx-auto rounded-full shadow-[0_0_10px_rgba(220,38,38,0.8)]" data-id="element-235" />
          <p className="text-gray-400 text-lg max-w-2xl mx-auto mt-4" data-id="element-236">
            Choose from our professional mobile detailing packages. Every
            service includes premium products, expert care, and the convenience
            of at-your-location service throughout Los Angeles and Ventura
            County.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-8" data-id="element-237">
          {services.map((service, index) => <ServiceCard key={index} service={service} index={index} data-id="element-238" />)}
        </div>
      </div>
    </section>;
}
function ServiceCard({
  service,
  index
}: {
  service: ServiceTier;
  index: number;
}) {
  const borderColor = service.color === 'blue' ? 'border-blue-500' : 'border-green-500';
  const shadowColor = service.color === 'blue' ? 'shadow-blue-500/20' : 'shadow-green-500/20';
  const titleColor = service.color === 'blue' ? 'text-blue-400' : 'text-green-400';
  const gradient = service.color === 'blue' ? 'from-blue-900/20 to-transparent' : 'from-green-900/20 to-transparent';
  return <motion.div initial={{
    y: 50,
    opacity: 0
  }} whileInView={{
    y: 0,
    opacity: 1
  }} viewport={{
    once: true
  }} transition={{
    delay: index * 0.2
  }} className={`relative flex flex-col bg-zinc-900/80 backdrop-blur-sm border-2 ${borderColor} rounded-lg overflow-hidden shadow-[0_0_30px_-5px_rgba(0,0,0,0.3)] ${shadowColor} hover:scale-[1.02] transition-transform duration-300`} data-id="element-239">
      {/* Header */}
      <div className={`p-6 bg-gradient-to-b ${gradient} border-b border-zinc-800`} data-id="element-240">
        <div className="flex items-center justify-between mb-2" data-id="element-241">
          <h3 className={`text-3xl font-black uppercase tracking-wider whitespace-nowrap ${titleColor}`} data-id="element-242">
            {service.title}
          </h3>
          <div className={`${titleColor}`} data-id="element-243">{service.icon}</div>
        </div>
        <p className="text-gray-400 text-sm mt-1" data-id="element-244">{service.description}</p>
      </div>

      {/* Features */}
      <div className="p-6 flex-1" data-id="element-245">
        <ul className="space-y-4" data-id="element-246">
          {service.features.map((feature, i) => <li key={i} className="flex items-start text-gray-300" data-id="element-247">
              <Check className="w-5 h-5 text-white mr-3 shrink-0" data-id="element-248" />
              <span className="font-medium text-lg" data-id="element-249">{feature}</span>
            </li>)}
        </ul>
      </div>

      {/* Pricing */}
      <div className="p-6 bg-zinc-950 border-t border-zinc-800" data-id="element-250">
        <div className="grid grid-cols-2 gap-4 text-center" data-id="element-251">
          <div className="flex flex-col" data-id="element-252">
            <span className="text-sm text-gray-500 uppercase font-bold tracking-wider mb-1" data-id="element-253">
              SM / MID
            </span>
            <span className={`text-4xl font-black ${titleColor}`} data-id="element-254">
              {service.priceSm}
            </span>
          </div>
          <div className="flex flex-col border-l border-zinc-800" data-id="element-255">
            <span className="text-sm text-gray-500 uppercase font-bold tracking-wider mb-1" data-id="element-256">
              SUV / CROSS
            </span>
            <span className={`text-4xl font-black ${titleColor}`} data-id="element-257">
              {service.priceSuv}
            </span>
          </div>
        </div>
      </div>
    </motion.div>;
}